import { useId } from "react";
import type { LucideIcon } from "lucide-react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDialogBehaviour } from "./useDialogBehaviour";

export interface ConfirmSheetProps {
  open: boolean;
  icon: LucideIcon;
  tone: "danger" | "secondary";
  title: string;
  message: string;
  confirmLabel: string;
  busy?: boolean;
  /** "sheet" rises from the bottom (in-flow actions); "center" is a modal. */
  placement?: "sheet" | "center";
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmSheet({
  open,
  icon: Icon,
  tone,
  title,
  message,
  confirmLabel,
  busy = false,
  placement = "sheet",
  onConfirm,
  onCancel,
}: ConfirmSheetProps) {
  const { t } = useTranslation();
  const id = useId();
  // Called before the early return: the dialog's keyboard contract is a hook,
  // so it has to run on every render whether the dialog is open or not.
  const panel = useDialogBehaviour({ open, onDismiss: onCancel, dismissible: !busy });

  if (!open) return null;

  const iconClass =
    tone === "danger" ? "bg-danger-bg text-accent-strong" : "bg-accent-soft text-accent";
  const confirmClass = tone === "danger" ? "bg-accent-strong" : "bg-accent";

  return (
    <div
      className={`fixed inset-y-0 left-1/2 z-50 flex w-full max-w-md -translate-x-1/2 justify-center bg-app-ink/40 p-4 backdrop-blur-md ${
        placement === "center" ? "items-center" : "items-end"
      }`}
      // Clicking the scrim dismisses, matching Escape — but not while the
      // action is in flight.
      onClick={busy ? undefined : onCancel}
    >
      <div
        ref={panel}
        className="flex w-full max-w-sm flex-col gap-4 rounded-xl bg-surface p-6 shadow-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-message`}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <span className={`mx-auto grid h-12 w-12 place-items-center rounded-full ${iconClass}`}>
          <Icon size={26} />
        </span>
        <div className="space-y-1 text-center">
          <h4 id={`${id}-title`} className="text-base font-semibold text-app-ink">
            {title}
          </h4>
          <p id={`${id}-message`} className="text-sm leading-6 text-app-copy">
            {message}
          </p>
        </div>
        <div className="flex flex-col gap-3 pt-1">
          <button
            type="button"
            className={`flex h-12 w-full items-center justify-center gap-2 rounded-xl text-base font-semibold text-content-on-accent transition-opacity active:opacity-90 disabled:opacity-60 ${confirmClass}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            {confirmLabel}
          </button>
          {/*
            Focus lands on Cancel, not Confirm: every dialog using this is a
            confirmation, and two of the three are destructive.
          */}
          <button
            type="button"
            data-autofocus
            className="h-12 w-full rounded-xl bg-app-field text-base font-semibold text-app-ink disabled:opacity-60"
            onClick={onCancel}
            disabled={busy}
          >
            {t("cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
